/**
 * @prettier
 */

import { useEffect, useState, useCallback } from "react";
import { useTranslation, Trans } from "react-i18next";
import { ModalPopupOverlay } from "../../../components/ModalPopupOverlay";
import FailedToAcceptDocumentIcon from "../../../assets/svg/DynamicColoredIcons/FailedToAcceptDocumentIcon";
import LowResolutionWarningIcon from "../../../assets/svg/DynamicColoredIcons/LowResolutionWarningIcon";
import ExpiredDocumentIcon from "../../../assets/svg/DynamicColoredIcons/ExpiredDocumentIcon";
import BlurryImageWarningIcon from "../../../assets/svg/DynamicColoredIcons/BlurryImageWarningIcon";
import GlareImageWarningIcon from "../../../assets/svg/DynamicColoredIcons/GlareImageWarningIcon";

const ValidationMessageModals = ({
    classification,
    validation,
    isAcceptedDoc,
    isDocMismatch,
    isCountryRestriction,
    restrictedCountryName,
    currentDocName,
    expired,
    submissionAttempts,
    failed,
    allowedDocumentsTypes = [],
}) => {
    const { t } = useTranslation();
    const [validationErrorDetails, setValidationErrorDetails] = useState(null);

    const getIconByValidationType = useCallback(
        validationErrorType => {
            let Icon = null;
            let modalTitle = "";
            let modalText = "";

            switch (validationErrorType) {
                case "glare":
                    Icon = GlareImageWarningIcon;
                    modalTitle = t("idpal_popup_validation_glare_title");
                    modalText = t("idpal_popup_validation_glare_text");
                    break;
                case "sharpness":
                    Icon = BlurryImageWarningIcon;
                    modalTitle = t("idpal_popup_validation_blurry_title");
                    modalText = t("idpal_popup_validation_blurry_text");
                    break;
                case "dpi":
                    Icon = LowResolutionWarningIcon;
                    modalTitle = t("idpal_popup_validation_resolution_title");
                    modalText = t("idpal_popup_validation_resolution_text");
                    break;
                default:
                    Icon = FailedToAcceptDocumentIcon;
                    modalTitle = t("idpal_popup_validation_glare_title");
                    modalText = t("idpal_popup_validation_glare_text");
            }

            return {
                Icon,
                modalTitle,
                modalText,
            };
        },
        [t]
    );

    useEffect(() => {
        if (!validation.validated && !failed) {
            setValidationErrorDetails(getIconByValidationType(validation.type));
        }
    }, [
        validation.validated,
        failed,
        validation.type,
        getIconByValidationType,
    ]);

    return (
        <>
            {submissionAttempts.remaining > 0 && !failed && (
                <>
                    {/* Failed classification */}
                    {!classification.classified && classification.messages && (
                        <ModalPopupOverlay
                            Icon={FailedToAcceptDocumentIcon}
                            popupTitle={t(
                                "idpal_popup_title_doc_not_recognized"
                            )}
                            popupText={
                                <Trans i18nKey="idpal_no_image_user_message" />
                            }
                        />
                    )}

                    {/* Failed on image quality */}
                    {classification.classified &&
                        !validation.validated &&
                        validationErrorDetails && (
                            <ModalPopupOverlay
                                Icon={validationErrorDetails.Icon}
                                popupTitle={validationErrorDetails.modalTitle}
                                popupText={validationErrorDetails.modalText}
                            />
                        )}
                    {/* Document Type not Accepted */}
                    {!isAcceptedDoc && (
                        <ModalPopupOverlay
                            Icon={FailedToAcceptDocumentIcon}
                            popupTitle={t("idpal_popup_title_doc_not_accepted")}
                            popupText={
                                isDocMismatch && isCountryRestriction ? (
                                    <Trans
                                        i18nKey="idpal_document_and_country_not_supported_modal"
                                        values={{
                                            country: restrictedCountryName,
                                            documents: currentDocName,
                                        }}
                                        components={{
                                            strong: <strong />,
                                            br: <br />,
                                        }}
                                    />
                                ) : isCountryRestriction ? (
                                    <Trans
                                        i18nKey="idpal_country_not_supported_modal"
                                        values={{
                                            country: restrictedCountryName,
                                        }}
                                        components={{
                                            strong: <strong />,
                                            br: <br />,
                                        }}
                                    />
                                ) : (
                                    <Trans
                                        i18nKey="idpal_document_not_supported_modal"
                                        values={{
                                            documents: allowedDocumentsTypes,
                                        }}
                                        components={{
                                            strong: <strong />,
                                            br: <br />,
                                        }}
                                    />
                                )
                            }
                        />
                    )}
                </>
            )}

            {/* Failed verification */}
            {failed && (
                <>
                    {expired ? (
                        <ModalPopupOverlay
                            Icon={ExpiredDocumentIcon}
                            popupTitle={t("idpal_popup_title_doc_expired")}
                            popupText={t(
                                "idpal_popup_text_expired_doc_warning"
                            )}
                        />
                    ) : (
                        <ModalPopupOverlay
                            Icon={FailedToAcceptDocumentIcon}
                            popupTitle={t("idpal_no_image_detected")}
                            popupText={t("idpal_no_image_user_message")}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default ValidationMessageModals;
